/**
 * Cheap XZ density splat of live fines → 128² RT → one blur → ground multiply.
 * No shadow maps. Cost is a point splat of the fine pool plus a 5-tap blur.
 */
import * as THREE from 'three';
import type { ArenaXZ } from './types';

export const SHADOW_SIZE = 128;

const SPLAT_VERT = /* glsl */ `
attribute float aId;
uniform sampler2D tPos;
uniform sampler2D tVel;
uniform sampler2D tMeta;
uniform float uTexSize;
uniform vec2 uArenaMin;
uniform vec2 uArenaSize;
uniform float uSplatAmt;
uniform float uPointScale;
uniform float uMaxPoint;
varying float vAmt;
void main() {
  vec2 tuv = (vec2(mod(aId, uTexSize), floor(aId / uTexSize)) + 0.5) / uTexSize;
  vec4 posAge = texture2D(tPos, tuv);
  vec4 velLife = texture2D(tVel, tuv);
  vec4 meta = texture2D(tMeta, tuv);
  if (posAge.w < 0.0) {
    vAmt = 0.0;
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    gl_PointSize = 1.0;
    return;
  }
  vec2 uv = (posAge.xz - uArenaMin) / uArenaSize;
  if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
    vAmt = 0.0;
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    gl_PointSize = 1.0;
    return;
  }
  float linger = max(velLife.w, 0.2);
  float life = clamp(posAge.w / linger, 0.0, 1.0);
  float size = max(meta.x, 0.08);
  vAmt = uSplatAmt * (1.0 - 0.45 * life) * smoothstep(0.08, 0.40, size);
  gl_Position = vec4(uv * 2.0 - 1.0, 0.0, 1.0);
  float worldToPx = 128.0 / max(min(uArenaSize.x, uArenaSize.y), 0.01);
  gl_PointSize = clamp(size * worldToPx * uPointScale, 2.0, uMaxPoint);
}
`;

const SPLAT_FRAG = /* glsl */ `
varying float vAmt;
void main() {
  if (vAmt < 1.0e-4) discard;
  vec2 p = gl_PointCoord * 2.0 - 1.0;
  float g = exp(-dot(p, p) * 2.6);
  float a = vAmt * g;
  gl_FragColor = vec4(a, a, a, a);
}
`;

const BLUR_VERT = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

const BLUR_FRAG = /* glsl */ `
uniform sampler2D tSrc;
uniform vec2 uTexel;
varying vec2 vUv;
void main() {
  vec4 acc = texture2D(tSrc, vUv) * 0.40;
  acc += texture2D(tSrc, vUv + vec2(uTexel.x, 0.0)) * 0.15;
  acc += texture2D(tSrc, vUv - vec2(uTexel.x, 0.0)) * 0.15;
  acc += texture2D(tSrc, vUv + vec2(0.0, uTexel.y)) * 0.15;
  acc += texture2D(tSrc, vUv - vec2(0.0, uTexel.y)) * 0.15;
  gl_FragColor = acc;
}
`;

export interface GroundShadowOpts {
  splatAmt?: number;
  pointScale?: number;
  maxPoint?: number;
}

export interface GroundShadow {
  texture: () => THREE.Texture;
  step(
    renderer: THREE.WebGLRenderer,
    posTex: THREE.Texture,
    velTex: THREE.Texture,
    metaTex: THREE.Texture,
    cloudTexSize: number,
    liveFines: number,
  ): void;
  clear(renderer: THREE.WebGLRenderer): void;
  dispose(): void;
}

function makeRt(): THREE.WebGLRenderTarget {
  const rt = new THREE.WebGLRenderTarget(SHADOW_SIZE, SHADOW_SIZE, {
    minFilter: THREE.LinearFilter,
    magFilter: THREE.LinearFilter,
    wrapS: THREE.ClampToEdgeWrapping,
    wrapT: THREE.ClampToEdgeWrapping,
    format: THREE.RGBAFormat,
    type: THREE.UnsignedByteType,
    depthBuffer: false,
    stencilBuffer: false,
  });
  rt.texture.generateMipmaps = false;
  rt.texture.name = 'particle-lab-cloud-shadow';
  return rt;
}

export function createGroundShadow(
  _renderer: THREE.WebGLRenderer,
  arena: ArenaXZ,
  capacity: number,
  opts: GroundShadowOpts = {},
): GroundShadow {
  const splatRt = makeRt();
  const blurRt = makeRt();

  const splatUniforms = {
    tPos: { value: null as THREE.Texture | null },
    tVel: { value: null as THREE.Texture | null },
    tMeta: { value: null as THREE.Texture | null },
    uTexSize: { value: 1 },
    uArenaMin: { value: new THREE.Vector2(arena.minX, arena.minZ) },
    uArenaSize: { value: new THREE.Vector2(arena.sizeX, arena.sizeZ) },
    uSplatAmt: { value: opts.splatAmt ?? 0.2 },
    uPointScale: { value: opts.pointScale ?? 2.4 },
    uMaxPoint: { value: opts.maxPoint ?? 18 },
  };
  const splatMat = new THREE.ShaderMaterial({
    uniforms: splatUniforms,
    vertexShader: SPLAT_VERT,
    fragmentShader: SPLAT_FRAG,
    transparent: true,
    depthTest: false,
    depthWrite: false,
    blending: THREE.CustomBlending,
    blendEquation: THREE.AddEquation,
    blendSrc: THREE.OneFactor,
    blendDst: THREE.OneFactor,
  });
  splatMat.toneMapped = false;

  const ids = new Float32Array(capacity);
  for (let i = 0; i < capacity; i += 1) ids[i] = i;
  const geom = new THREE.BufferGeometry();
  geom.setAttribute('aId', new THREE.BufferAttribute(ids, 1));
  geom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(capacity * 3), 3));
  const points = new THREE.Points(geom, splatMat);
  points.frustumCulled = false;
  const splatScene = new THREE.Scene();
  splatScene.add(points);
  const splatCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

  const blurUniforms = {
    tSrc: { value: splatRt.texture as THREE.Texture },
    uTexel: { value: new THREE.Vector2(1 / SHADOW_SIZE, 1 / SHADOW_SIZE) },
  };
  const blurMat = new THREE.ShaderMaterial({
    uniforms: blurUniforms,
    vertexShader: BLUR_VERT,
    fragmentShader: BLUR_FRAG,
    depthTest: false,
    depthWrite: false,
  });
  blurMat.toneMapped = false;
  const blurScene = new THREE.Scene();
  const blurMesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), blurMat);
  blurScene.add(blurMesh);

  let disposed = false;

  const run = (
    renderer: THREE.WebGLRenderer,
    posTex: THREE.Texture,
    velTex: THREE.Texture,
    metaTex: THREE.Texture,
    cloudTexSize: number,
    liveFines: number,
  ) => {
    if (disposed) return;
    const prev = renderer.getRenderTarget();
    const prevAuto = renderer.autoClear;
    const prevClear = new THREE.Color();
    renderer.getClearColor(prevClear);
    const prevAlpha = renderer.getClearAlpha();
    renderer.autoClear = true;
    renderer.setClearColor(0x000000, 0);
    renderer.setRenderTarget(splatRt);
    renderer.clear(true, true, true);
    if (liveFines > 0) {
      splatUniforms.tPos.value = posTex;
      splatUniforms.tVel.value = velTex;
      splatUniforms.tMeta.value = metaTex;
      splatUniforms.uTexSize.value = cloudTexSize;
      renderer.autoClear = false;
      renderer.render(splatScene, splatCam);
    }
    renderer.autoClear = true;
    blurUniforms.tSrc.value = splatRt.texture;
    renderer.setRenderTarget(blurRt);
    renderer.render(blurScene, splatCam);
    renderer.setRenderTarget(prev);
    renderer.autoClear = prevAuto;
    renderer.setClearColor(prevClear, prevAlpha);
  };

  return {
    texture: () => blurRt.texture,
    step: run,
    clear(renderer) {
      const prev = renderer.getRenderTarget();
      const prevClear = new THREE.Color();
      renderer.getClearColor(prevClear);
      const prevAlpha = renderer.getClearAlpha();
      renderer.setClearColor(0x000000, 0);
      renderer.setRenderTarget(splatRt);
      renderer.clear(true, true, true);
      renderer.setRenderTarget(blurRt);
      renderer.clear(true, true, true);
      renderer.setRenderTarget(prev);
      renderer.setClearColor(prevClear, prevAlpha);
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      splatRt.dispose();
      blurRt.dispose();
      splatMat.dispose();
      blurMat.dispose();
      geom.dispose();
      blurMesh.geometry.dispose();
    },
  };
}
